/**
 * `sources.name` → a short, seeker-facing label for the "via {source}" line
 * on the job card (job-ux-spec H3: the row never showed where a job came
 * from, only an internal `source_platform` analytics tag).
 *
 * Zero imports on purpose — this sits next to `job-row.tsx`, which is on the
 * feed's client bundle; a leaf module keeps it out of any dependency-budget
 * conversation.
 *
 * Real values seen in PostHog `job_apply_outbound_clicked.source_platform`,
 * last 30 days (2026-09-26): "Ashby Job Boards", "Adzuna", "Careerjet",
 * "Jooble", "Lever Postings", "The Muse", "NoDesk", "Greenhouse Boards",
 * "Manatal Career Pages", "LinkedIn", "WeWorkRemotely",
 * "Cryptocurrency Jobs", "HN Who is Hiring", "RemoteOK", "Jobicy",
 * "Recruitee", "Workew", "SmartRecruiters", "4dayweek.io",
 * "Fellowship RSS (OpportunityDesk + Opportunities For Youth)", "Arbeitnow",
 * "Working Nomads", "ReliefWeb", "Jobg8 (United States)", plus junk values
 * ("hunt", "unknown") and raw ids ("src_careerjet") that must never reach a
 * seeker.
 */

/** Matches a trailing parenthetical, e.g. "Jobg8 (United States)" → "Jobg8". */
const TRAILING_PAREN_RE = /\s*\([^)]*\)\s*$/;

/**
 * Suffixes that are internal ATS/board vocabulary, not something a seeker
 * recognizes as part of the source's name. Stripped once, case-insensitive,
 * longest-first so " Career Pages" doesn't leave a dangling " Pages" match
 * from a shorter entry (there is no such overlap today, but order still
 * matters if the list grows).
 */
const TRAILING_SUFFIXES = [
	" Job Boards",
	" Career Pages",
	" Boards",
	" Postings",
	" RSS",
];

/**
 * Partners we carry as a SILENT partner: the commercial agreement forbids
 * naming them anywhere a seeker can see. Jobg8 support asked for every
 * reference to be removed (2026-10-11) after spotting "via Jobg8" on our job
 * cards. Matched on the raw `sources.name` prefix so every regional feed
 * ("Jobg8 (United States)", future "Jobg8 (UK)") is covered.
 */
const SILENT_SOURCE_RE = /^jobg8\b/i;

/**
 * True when `sources.name` belongs to a silent partner. Seeker-facing code
 * must render no source attribution for these rows (not even a fallback like
 * "source"); analytics and admin surfaces may keep the raw name.
 */
export function isSilentSource(name: string | null | undefined): boolean {
	return Boolean(name && SILENT_SOURCE_RE.test(name.trim()));
}

/** Values that are never a real source name — junk or an internal id. */
function isJunkValue(v: string): boolean {
	const lower = v.toLowerCase();
	return (
		lower === "" || lower === "unknown" || lower === "hunt" || /^src_/i.test(v)
	);
}

/**
 * Renamed to the name seekers actually recognize — "HN Who is Hiring" is the
 * feed's internal listing name, "Hacker News" is the site.
 */
const RENAME: Record<string, string> = {
	"HN Who is Hiring": "Hacker News",
};

/**
 * `sources.name` → a display label, or `null` when nothing renderable
 * survives (junk value, internal id, silent partner, or an empty result after
 * stripping).
 */
export function sourceDisplayName(
	name: string | null | undefined,
): string | null {
	if (!name) return null;
	const trimmed = name.trim();
	if (isJunkValue(trimmed) || isSilentSource(trimmed)) return null;

	let result = trimmed.replace(TRAILING_PAREN_RE, "");

	for (const suffix of TRAILING_SUFFIXES) {
		if (result.toLowerCase().endsWith(suffix.toLowerCase())) {
			result = result.slice(0, result.length - suffix.length);
			break;
		}
	}

	result = result.trim();
	if (!result) return null;

	return RENAME[result] ?? result;
}
